import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
  BatteryProperties,
  DcSensorValueProperties,
  DeviceConnectionStatus,
  DeviceConnectionStatusValues,
  DeviceFirmwareVersion,
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  DeviceNameCommands,
  DeviceNameProperties,
  EtrvMotorPositionProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsActiveProperty,
  IsNumberValueProperty,
  RoomOpenPointDetectionActivityProperties,
  SignalProperties,
} from '@vi-iot/client';

import { lowBatteryThreshold, lowLqiSignalThreshold } from '@common';
import { NO_VALUE } from '@pipes';
import { ActiveErrorData, ActiveErrorsStore, InstallationFeaturesStore } from '@store';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GTMService } from '@services';
import { FeatureValuePipe } from '../../../operational-data-common/feature-pipes/feature-value.pipe';
import { PillType } from '../../../shared/components/pill/pill.model';
import { InstallationService } from '../../services/installation.service';
import { EditDeviceNameModalComponent } from '../edit-device-name-modal/edit-device-name-modal.component';
import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-trv-panel',
  templateUrl: './trv-panel.component.html',
  styleUrls: ['./trv-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrvPanelComponent implements OnInit {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  readonly featurePropertyKey = FeaturePropertyKey;

  sensorTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  temperature$: Observable<Feature<DcSensorValueProperties, never>>;
  battery$: Observable<Feature<BatteryProperties, never>>;
  wifiLqi$: Observable<Feature<SignalProperties, never>>;
  motorPosition$: Observable<Feature<EtrvMotorPositionProperties, never>>;
  status$: Observable<Feature<DeviceConnectionStatus, never>>;
  heatingCircuitId$: Observable<Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>>;
  mountingMode$: Observable<Feature<IsActiveProperty, never>>;
  openPointDetection$: Observable<Feature<RoomOpenPointDetectionActivityProperties, never>>;
  deviceErrors$: Observable<ActiveErrorData[]>;
  deviceName$: Observable<Feature<DeviceNameProperties, DeviceNameCommands>>;
  editDeviceNameDialogRef: MatDialogRef<EditDeviceNameModalComponent>;
  firmware$: Observable<Feature<DeviceFirmwareVersion, never>>;

  featuresLoading$: Observable<boolean>;
  isSrcStandalone$: Observable<Feature<IsActiveProperty, never>>;

  connectionStatus = DeviceConnectionStatusValues;
  isOpenPointDetectionActive: boolean;

  noValue = NO_VALUE;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly lowBatteryThreshold = lowBatteryThreshold;
  readonly pillType = PillType;

  @Input() deviceId: string;
  @Input() roomId: string;

  constructor(
    public activeErrorsStore: ActiveErrorsStore,
    public installationService: InstallationService,
    private featureValuePipe: FeatureValuePipe,
    private dialog: MatDialog,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.isSrcStandalone$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.standaloneMode,
      DeviceIds.RoomControl
    );
    this.sensorTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceTemperatureSensor,
      this.deviceId
    );
    this.temperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.etrvTemperature,
      this.deviceId
    );
    this.battery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.devicePowerBattery,
      this.deviceId
    );
    this.wifiLqi$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceZigbeeLqi,
      this.deviceId
    );

    this.motorPosition$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.etrvValvePosition,
      this.deviceId
    );
    this.featuresLoading$ = this.installationFeaturesStore.featuresLoading$;
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      this.deviceId
    );
    this.heatingCircuitId$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceHeatingCircuitId,
      this.deviceId
    );
    this.mountingMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsActiveProperty,
      never
    >(FeatureNames.etrvMountingMode, this.deviceId);

    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
      this.deviceId
    );
    this.deviceName$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, this.deviceId);

    this.deviceErrors$ = this.activeErrorsStore.getActiveErrorsForSRCDevice(this.deviceId);
    this.openPointDetection$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<RoomOpenPointDetectionActivityProperties, never>(
        FeatureNames.roomOpenPointDetectionActivity(this.roomId),
        DeviceIds.RoomControl
      )
      .pipe(
        tap((feature: Feature<RoomOpenPointDetectionActivityProperties, never>) => {
          const activityValue: number = this.featureValuePipe.transform(feature, 'value');
          this.isOpenPointDetectionActive = !!activityValue && activityValue > 0;
        })
      );
  }

  openEditDeviceNameDialog(deviceId: string): void {
    this.gtmService.track('onOpen', 'Edit Device Name modal', 'singleRoomControl');
    this.editDeviceNameDialogRef = this.dialog.open(EditDeviceNameModalComponent, {
      width: '715px',
      panelClass: 'commissioning-edit-room-dialog',
      data: {
        deviceId,
      },
    });
  }
}
